import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { TopTabs } from "@/components/PageHeader";
import { ListToolbar, FilterBox, RadioGroup, DataTable, exportCsv } from "@/components/ListTools";
import { Sheet as SheetIcon, Archive, ArchiveRestore, Trash2, Plus, Pencil, FileText } from "lucide-react";
import { CourtDialog } from "@/components/CourtDialog";
import { RegisteredAgentDialog } from "@/components/RegisteredAgentDialog";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useSelection, BulkBar } from "@/components/BulkSelect";

const ACTIVE = new Set(["pending", "in_progress"]);

const CASE_STATE_OPTIONS = [{ value: "all", label: "All" }, { value: "active", label: "Active" }, { value: "archived", label: "Archived" }];
const CASES_TABS = [{ label: "Court Cases" }, { label: "Courts" }, { label: "Registered Agents" }];

export default function CourtCases() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [tab, setTab] = useState("Court Cases");
  const [q, setQ] = useState("");
  const [state, setState] = useState("active");
  const [range, setRange] = useState("all");
  const [filtersOpen, setFiltersOpen] = useState(true);
  const navigate = useNavigate();
  useEffect(() => { api.get("/jobs").then((r) => setJobs(r.data)); }, []);

  const cases = useMemo(() => {
    const map = {};
    for (const j of jobs) {
      const key = j.court_case_number || `(no case #) ${j.job_number}`;
      const c = map[key] || (map[key] = { key, case_number: j.court_case_number, plaintiff: j.plaintiff, defendant: j.recipient_name, court: j.court_name, court_date: j.due_date, jobs: [], active: false, created_at: j.created_at });
      c.jobs.push(j);
      if (ACTIVE.has(j.status)) c.active = true;
      if (!c.plaintiff && j.plaintiff) c.plaintiff = j.plaintiff;
      if (!c.court && j.court_name) c.court = j.court_name;
      if (j.created_at > c.created_at) c.created_at = j.created_at;
    }
    return Object.values(map).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  }, [jobs]);

  const filtered = cases.filter((c) => {
    if (state === "active" && !c.active) return false;
    if (state === "archived" && !c.jobs.every((j) => j.status === "cancelled")) return false;
    if (state === "all" && c.jobs.every((j) => j.status === "cancelled")) return false;
    if (range !== "all") {
      const days = Number(range);
      if (new Date(c.created_at) < new Date(Date.now() - days * 864e5)) return false;
    }
    if (!q) return true;
    const t = q.toLowerCase();
    return [c.case_number, c.plaintiff, c.defendant, c.court].some((v) => v?.toLowerCase().includes(t));
  });

  const [courts, setCourts] = useState([]);
  const [courtEdit, setCourtEdit] = useState(null);
  const loadCourts = () => api.get("/courts").then((r) => setCourts(r.data)).catch(() => {});
  useEffect(() => { if (tab === "Courts") loadCourts(); }, [tab]);
  const deleteCourt = async (c) => {
    if (!window.confirm(`Remove "${c.name}" from the directory? Existing jobs are not changed.`)) return;
    try { await api.delete(`/courts/${c.id}`); toast.success("Court removed"); loadCourts(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to remove"); }
  };

  const [agents, setAgents] = useState([]);
  const [agentEdit, setAgentEdit] = useState(null);
  const loadAgents = () => api.get("/registered-agents").then((r) => setAgents(r.data)).catch(() => {});
  useEffect(() => { if (tab === "Registered Agents") loadAgents(); }, [tab]);
  const deleteAgent = async (a) => {
    if (!window.confirm(`Remove "${a.name}" from the directory? Existing jobs are not changed.`)) return;
    try { await api.delete(`/registered-agents/${a.id}`); toast.success("Registered agent removed"); loadAgents(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to remove"); }
  };

  const selection = useSelection(filtered.map((c) => c.key));
  const jobIdsFor = (keys) => cases.filter((c) => keys.includes(c.key)).flatMap((c) => c.jobs.map((j) => j.id));
  const bulk = async (keys, action, msg) => {
    try { await api.post("/jobs/bulk", { ids: jobIdsFor(keys), action }); toast.success(`${keys.length} case(s) ${msg}`); api.get("/jobs").then((r) => setJobs(r.data)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Bulk action failed"); }
  };
  const bulkActions = [
    state === "archived" ? { label: "Restore", icon: ArchiveRestore, onClick: (k) => bulk(k, "restore", "restored") }
      : { label: "Archive", icon: Archive, confirm: "Archive {n} case(s)? All jobs in these cases will be archived.", onClick: (k) => bulk(k, "archive", "archived") },
    ...(user?.role === "admin" ? [{ label: "Delete", icon: Trash2, className: "text-rose-600 border-rose-200 hover:bg-rose-50", confirm: "Permanently delete {n} case(s) and every job, attempt and affidavit in them?", onClick: (k) => bulk(k, "delete", "deleted") }] : []),
  ];

  const columns = [
    { key: "case", label: "Case", render: (c) => <Link to={`/jobs/${c.jobs[0].id}`} className="sm-link font-semibold" data-testid={`case-link-${c.key}`}>{c.case_number || `Job ${c.jobs[0].job_number}`}</Link> },
    { key: "plaintiff", label: "Plaintiff", render: (c) => <span className="uppercase">{c.plaintiff || "—"}</span> },
    { key: "defendant", label: "Defendant", render: (c) => <span className="uppercase">{c.defendant || "—"}</span> },
    { key: "court", label: "Court Name", render: (c) => <span className="uppercase">{c.court || "—"}</span> },
    { key: "date", label: "Court Date", className: "text-right whitespace-nowrap", render: (c) => c.court_date ? new Date(c.court_date).toLocaleDateString() : "—" },
    { key: "jobs", label: "Jobs", className: "text-right", render: (c) => c.jobs.length },
  ];

  return (
    <div data-testid="court-cases-page">
      <TopTabs tabs={CASES_TABS} value={tab} onChange={setTab} />
      <div className="px-4 sm:px-7 py-6 space-y-6">
        {tab === "Court Cases" ? (
          <>
            <ListToolbar title="Court Cases" search={q} onSearch={setQ} placeholder="Case, Plaintiff, Defendant, Court"
              filtersOpen={filtersOpen} onToggleFilters={() => setFiltersOpen((o) => !o)} onClear={() => { setQ(""); setState("all"); setRange("all"); }}
              action={<Button className="sm-green" data-testid="new-case-button" onClick={() => navigate("/jobs/new")}>New Case</Button>} />
            {filtersOpen && (
              <div className="flex gap-6 flex-wrap">
                <FilterBox title="Court Cases">
                  <RadioGroup name="cases" value={state} onChange={setState} options={CASE_STATE_OPTIONS} />
                </FilterBox>
                <FilterBox title="Date Range">
                  <select value={range} onChange={(e) => setRange(e.target.value)} className="w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-sm" data-testid="date-range-select">
                    <option value="all">—</option><option value="7">Last 7 days</option><option value="30">Last 30 days</option><option value="90">Last 90 days</option><option value="365">Last year</option>
                  </select>
                </FilterBox>
              </div>
            )}
            <div className="flex items-center gap-4 text-sm text-slate-600">
              <span data-testid="cases-count">{filtered.length} of {cases.length} Cases</span>
              <button type="button" className="flex items-center gap-1 hover:text-slate-900" data-testid="export-cases-button"
                onClick={() => exportCsv("court-cases.csv", ["Case", "Plaintiff", "Defendant", "Court", "Court Date", "Jobs"], filtered.map((c) => [c.case_number, c.plaintiff, c.defendant, c.court, c.court_date, c.jobs.length]))}>
                <SheetIcon className="h-4 w-4" /> Export Spreadsheet
              </button>
            </div>
            <BulkBar selection={selection} noun="case" actions={bulkActions} />
            <DataTable columns={columns} rows={filtered} rowKey={(c) => c.key} emptyText="No cases match." testId="court-cases-table" selection={selection} />
          </>
        ) : tab === "Registered Agents" ? (
          <>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h1 className="text-2xl font-semibold tracking-tight">Registered Agents</h1>
                <p className="text-sm text-slate-500 mt-0.5">Agents you serve regularly. They're remembered automatically from jobs; keep the address, hours and the usual intake employee here so every future job and attempt is pre-filled.</p>
              </div>
              {user?.role !== "client" && <Button className="sm-green" onClick={() => setAgentEdit({})} data-testid="new-agent-button"><Plus className="h-4 w-4 mr-1" />New Registered Agent</Button>}
            </div>
            <DataTable testId="agents-table" rowKey={(a) => a.id} rows={agents} emptyText="No registered agents yet. Add one, or they'll appear as you create jobs with the Registered Agent recipient type."
              columns={[
                { key: "name", label: "Registered agent", render: (a) => <button type="button" className="font-medium text-left sm-link" onClick={() => setAgentEdit(a)} data-testid={`agent-row-${a.id}`}>{a.name}</button> },
                { key: "address", label: "Address", render: (a) => <span className="text-slate-700">{[a.address, [a.city, a.state].filter(Boolean).join(", "), a.zip].filter(Boolean).join(" · ") || <span className="text-amber-700 text-xs">No address yet — click to add</span>}</span> },
                { key: "hours", label: "Hours", render: (a) => a.hours || "—" },
                { key: "contact", label: "Employees", render: (a) => a.contacts?.length ? <div className="flex flex-wrap gap-1">{a.contacts.slice(0, 3).map((c, i) => <span key={i} className="inline-flex rounded-full bg-slate-100 border border-slate-200 px-2 py-0.5 text-[11px]">{c.person_name}{c.person_title ? <span className="text-slate-500">&nbsp;· {c.person_title}</span> : ""}</span>)}{a.contacts.length > 3 && <span className="text-[11px] text-slate-500">+{a.contacts.length - 3} more</span>}</div> : a.last_person?.person_name ? <span className="text-slate-500 text-xs">Last served: {a.last_person.person_name}{a.last_person.person_title ? ` (${a.last_person.person_title})` : ""} — click to save</span> : "—" },
                { key: "phone", label: "Phone", render: (a) => a.phone || "—" },
                { key: "jobs", label: "Jobs", className: "text-right", render: (a) => a.jobs },
                { key: "actions", label: "", className: "text-right", render: (a) => user?.role !== "client" && (
                  <div className="flex justify-end gap-3 text-xs">
                    <button type="button" className="sm-link inline-flex items-center gap-1" onClick={() => setAgentEdit(a)} data-testid={`agent-edit-${a.id}`}><Pencil className="h-3 w-3" />Edit</button>
                    {user?.role === "admin" && <button type="button" className="text-rose-600 hover:underline inline-flex items-center gap-1" onClick={() => deleteAgent(a)} data-testid={`agent-delete-${a.id}`}><Trash2 className="h-3 w-3" />Remove</button>}
                  </div>
                ) },
              ]} />
            {agentEdit && <RegisteredAgentDialog agent={agentEdit} onClose={() => setAgentEdit(null)} onSaved={() => { setAgentEdit(null); loadAgents(); }} />}
          </>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h1 className="text-2xl font-semibold tracking-tight">Courts</h1>
                <p className="text-sm text-slate-500 mt-0.5">Your court directory. Courts are remembered automatically from jobs; fix an address here once and it's suggested correctly on every future job.</p>
              </div>
              {user?.role !== "client" && <Button className="sm-green" onClick={() => setCourtEdit({})} data-testid="new-court-button"><Plus className="h-4 w-4 mr-1" />New Court</Button>}
            </div>
            <DataTable testId="courts-table" rowKey={(c) => c.id} rows={courts} emptyText="No courts yet. Add one, or they'll appear as you create jobs."
              columns={[
                { key: "name", label: "Court", render: (c) => <button type="button" className="font-medium text-left sm-link" onClick={() => setCourtEdit(c)} data-testid={`court-row-${c.id}`}>{c.name}</button> },
                { key: "address", label: "Address", render: (c) => <span className="text-slate-700">{[c.address, [c.city, c.state].filter(Boolean).join(", "), c.zip].filter(Boolean).join(" · ") || <span className="text-amber-700 text-xs">No address yet — click to add</span>}</span> },
                { key: "county", label: "County", render: (c) => c.county || "—" },
                { key: "phone", label: "Phone", render: (c) => c.phone || "—" },
                { key: "form", label: "Affidavit form", render: (c) => c.affidavit_form_id ? <span className="text-xs text-emerald-800 inline-flex items-center gap-1" data-testid={`court-form-linked-${c.id}`}><FileText className="h-3 w-3" />Official PDF linked</span> : <span className="text-xs text-slate-400">Simply Served default</span> },
                { key: "jobs", label: "Jobs", className: "text-right", render: (c) => c.jobs },
                { key: "actions", label: "", className: "text-right", render: (c) => user?.role !== "client" && (
                  <div className="flex justify-end gap-3 text-xs">
                    <button type="button" className="sm-link inline-flex items-center gap-1" onClick={() => setCourtEdit(c)} data-testid={`court-edit-${c.id}`}><Pencil className="h-3 w-3" />Edit</button>
                    {user?.role === "admin" && <button type="button" className="text-rose-600 hover:underline inline-flex items-center gap-1" onClick={() => deleteCourt(c)} data-testid={`court-delete-${c.id}`}><Trash2 className="h-3 w-3" />Remove</button>}
                  </div>
                ) },
              ]} />
            {courtEdit && <CourtDialog court={courtEdit} onClose={() => setCourtEdit(null)} onSaved={() => { setCourtEdit(null); loadCourts(); }} />}
          </>
        )}
      </div>
    </div>
  );
}
